import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Enrollment, Schedule, Course, Subscription, Instructor } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatDate, getUpcomingScheduledSessions } from '../../utils/dateUtils';
import {
  RenewalStatus,
  compareByExpiration,
  daysUntilExpiration,
  getRenewalLabel,
  getRenewalStatus,
  getSubscriptionUtilization,
  isExpiringSoon,
} from '../../utils/renewalStatus';

interface DashboardStats {
  totalCourses: number;
  totalSchedules: number;
  totalEnrollments: number;
  totalRevenue: number;
  activeSubscriptions: number;
  pendingOnboarding: number;
  totalInstructors: number;
  expiringSoon: number;
  expired: number;
}

interface ExpiringRow {
  sub: Subscription;
  renewalStatus: RenewalStatus;
  daysLeft: number;
  utilization: ReturnType<typeof getSubscriptionUtilization>;
}

const AdminDashboard: React.FC = () => {
  usePageTitle('Admin Dashboard');
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    totalSchedules: 0,
    totalEnrollments: 0,
    totalRevenue: 0,
    activeSubscriptions: 0,
    pendingOnboarding: 0,
    totalInstructors: 0,
    expiringSoon: 0,
    expired: 0,
  });
  const [recentEnrollments, setRecentEnrollments] = useState<Enrollment[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<Schedule[]>([]);
  const [pendingSubs, setPendingSubs] = useState<Subscription[]>([]);
  const [expiringRows, setExpiringRows] = useState<ExpiringRow[]>([]);
  const [courses, setCourses] = useState<Record<string, Course>>({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const fetchData = useCallback(async () => {
    try {
      const [coursesRes, schedulesRes, enrollmentsRes, subsRes, instructorsRes] = await Promise.all([
        authFetch('/api/courses'),
        authFetch('/api/schedules'),
        authFetch('/api/enrollments'),
        authFetch('/api/subscriptions'),
        authFetch('/api/instructors'),
      ]);

      let courseList: Course[] = [];
      let scheduleList: Schedule[] = [];
      let enrollmentList: Enrollment[] = [];
      let subList: Subscription[] = [];
      let instructorList: Instructor[] = [];

      if (coursesRes.ok) {
        courseList = await coursesRes.json();
        const map: Record<string, Course> = {};
        courseList.forEach((c) => (map[c.id] = c));
        setCourses(map);
      }
      if (schedulesRes.ok) scheduleList = await schedulesRes.json();
      if (enrollmentsRes.ok) enrollmentList = await enrollmentsRes.json();
      if (subsRes.ok) subList = await subsRes.json();
      if (instructorsRes.ok) instructorList = await instructorsRes.json();

      const pending = subList.filter(
        (s) => s.status === 'payment_complete_pending_account'
      );
      setPendingSubs(pending);

      const now = new Date();
      const rows: ExpiringRow[] = subList
        .filter((s) => s.status === 'active')
        .map((sub) => ({
          sub,
          renewalStatus: getRenewalStatus(sub, now),
          daysLeft: daysUntilExpiration(sub, now),
          utilization: getSubscriptionUtilization(sub, enrollmentList),
        }))
        .filter((r) => r.renewalStatus !== 'healthy' && r.renewalStatus !== 'n/a')
        .sort((a, b) => compareByExpiration(a.sub, b.sub));
      setExpiringRows(rows);

      const expiringCount = rows.filter((r) => isExpiringSoon(r.renewalStatus)).length;
      const expiredCount = rows.filter((r) => r.renewalStatus === 'expired').length;

      setStats({
        totalCourses: courseList.filter((c) => c.active).length,
        totalSchedules: scheduleList.filter((s) => s.status === 'scheduled').length,
        totalEnrollments: enrollmentList.filter((e) => e.status === 'active').length,
        totalRevenue: 0,
        activeSubscriptions: subList.filter((s) => s.status === 'active').length,
        pendingOnboarding: pending.length,
        totalInstructors: instructorList.length,
        expiringSoon: expiringCount,
        expired: expiredCount,
      });

      setRecentEnrollments(
        enrollmentList
          .sort((a, b) => new Date(b.enrollmentDate).getTime() - new Date(a.enrollmentDate).getTime())
          .slice(0, 5)
      );

      setUpcomingSessions(getUpcomingScheduledSessions(scheduleList).slice(0, 5));
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (id: string) => {
    if (!window.confirm('Approve this subscription? The customer will receive a welcome email and approval notification.')) return;
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/subscriptions/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(`Failed to approve: ${err.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error approving subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User clicked Cancel
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/subscriptions/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || undefined }),
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(`Failed to reject: ${err.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error rejecting subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">Platform overview and management</p>
        </div>
      </section>

      {/* Pending Onboarding Alert */}
      {pendingSubs.length > 0 && (
        <section className="section">
          <div className="section-container">
            <div className="alert alert-warning">
              <span className="alert-icon">&#9888;</span>
              <div>
                <strong>{pendingSubs.length} subscription(s) pending onboarding</strong>
                <p>These customers have completed payment but need account provisioning.</p>
                <div className="alert-items">
                  {pendingSubs.map((sub) => (
                    <div key={sub.id} className="alert-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                      <span>{sub.customerName} ({sub.customerEmail}) - {sub.tier} plan — {sub.paymentMethod.replace(/_/g, ' ')}</span>
                      <span style={{ display: 'flex', gap: '8px', marginLeft: '12px', flexShrink: 0 }}>
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: 'var(--green, #28a745)', color: 'white', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}
                          onClick={() => handleApprove(sub.id)}
                          disabled={actionLoading === sub.id}
                        >
                          {actionLoading === sub.id ? '...' : 'Approve'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          style={{ padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}
                          onClick={() => handleReject(sub.id)}
                          disabled={actionLoading === sub.id}
                        >
                          Reject
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Renewal Urgency Alert */}
      {(stats.expired > 0 || expiringRows.some((r) => r.renewalStatus === '30-day')) && (
        <section className="section">
          <div className="section-container">
            <div className="alert alert-danger">
              <span className="alert-icon">&#9888;</span>
              <div>
                <strong>
                  {stats.expired > 0 && `${stats.expired} expired`}
                  {stats.expired > 0 && expiringRows.some((r) => r.renewalStatus === '30-day') && ', '}
                  {expiringRows.filter((r) => r.renewalStatus === '30-day').length > 0 &&
                    `${expiringRows.filter((r) => r.renewalStatus === '30-day').length} expiring within 30 days`}
                </strong>
                <p>Reach out to these customers to drive renewal and protect revenue.</p>
                <Link to="/admin/subscriptions?renewal=expiring-soon" className="btn btn-outline btn-sm">
                  View Expiring Subscriptions
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      <section className="section">
        <div className="section-container">
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <span className="admin-stat-number">{stats.totalCourses}</span>
              <span className="admin-stat-label">Active Courses</span>
              <Link to="/admin/courses" className="admin-stat-link">Manage</Link>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-number">{stats.totalSchedules}</span>
              <span className="admin-stat-label">Upcoming Sessions</span>
              <Link to="/admin/schedules" className="admin-stat-link">Manage</Link>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-number">{stats.totalEnrollments}</span>
              <span className="admin-stat-label">Active Enrollments</span>
              <Link to="/admin/enrollments" className="admin-stat-link">Manage</Link>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-number">{stats.totalInstructors}</span>
              <span className="admin-stat-label">Instructors</span>
              <Link to="/admin/instructors" className="admin-stat-link">Manage</Link>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-number">{stats.activeSubscriptions}</span>
              <span className="admin-stat-label">Active Subscriptions</span>
              <Link to="/admin/subscriptions?status=active" className="admin-stat-link">Manage</Link>
            </div>
            <div className="admin-stat-card admin-stat-alert">
              <span className="admin-stat-number">{stats.pendingOnboarding}</span>
              <span className="admin-stat-label">Pending Onboarding</span>
              <Link to="/admin/subscriptions" className="admin-stat-link">Manage</Link>
            </div>
            <div className={`admin-stat-card${stats.expiringSoon + stats.expired > 0 ? ' admin-stat-alert' : ''}`}>
              <span className="admin-stat-number">{stats.expiringSoon + stats.expired}</span>
              <span className="admin-stat-label">Expiring / Expired</span>
              <Link to="/admin/subscriptions?renewal=expiring-soon" className="admin-stat-link">Review</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="section section-alt">
        <div className="section-container">
          <h2 className="section-title">Quick Actions</h2>
          <div className="quick-actions">
            <Link to="/admin/courses" className="quick-action-btn">
              <span className="qa-icon">&#128218;</span> Manage Courses
            </Link>
            <Link to="/admin/tracks" className="quick-action-btn">
              <span className="qa-icon">&#128739;</span> Manage Tracks
            </Link>
            <Link to="/admin/schedules" className="quick-action-btn">
              <span className="qa-icon">&#128197;</span> Manage Schedules
            </Link>
            <Link to="/admin/enrollments" className="quick-action-btn">
              <span className="qa-icon">&#128101;</span> Manage Enrollments
            </Link>
            <Link to="/admin/instructors" className="quick-action-btn">
              <span className="qa-icon">&#128100;</span> Manage Instructors
            </Link>
            <Link to="/admin/subscriptions" className="quick-action-btn">
              <span className="qa-icon">&#128203;</span> Manage Subscriptions
            </Link>
            <Link to="/admin/discounts" className="quick-action-btn">
              <span className="qa-icon">&#127991;</span> Discount Codes
            </Link>
            <Link to="/admin/add-student" className="quick-action-btn" style={{ backgroundColor: 'var(--green, #28a745)', color: 'white' }}>
              <span className="qa-icon">&#10133;</span> Add Student
            </Link>
            <button
              className="quick-action-btn"
              style={{ backgroundColor: '#2ca01c', color: 'white', border: 'none', cursor: 'pointer' }}
              onClick={async () => {
                try {
                  const res = await authFetch('/api/qbo/authorize', { method: 'POST' });
                  if (res.ok) {
                    const data = await res.json();
                    window.location.href = data.redirectUrl;
                  } else {
                    const err = await res.json();
                    alert(`Failed to start QBO authorization: ${err.error || res.statusText}`);
                  }
                } catch (err) {
                  alert('Failed to start QBO authorization. Check console for details.');
                  console.error('QBO authorize error:', err);
                }
              }}
            >
              <span className="qa-icon">&#128279;</span> Re-authorize QuickBooks
            </button>
          </div>
        </div>
      </section>

      {/* Recent Enrollments */}
      <section className="section">
        <div className="section-container">
          <div className="section-header-row">
            <h2 className="section-title">Recent Enrollments</h2>
            <Link to="/admin/enrollments" className="btn btn-outline btn-sm">View All</Link>
          </div>
          {recentEnrollments.length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Email</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentEnrollments.map((e) => (
                  <tr key={e.id}>
                    <td>{e.studentName}</td>
                    <td>{e.studentEmail}</td>
                    <td>{formatDate(e.enrollmentDate)}</td>
                    <td><span className={`status-badge status-${e.status}`}>{e.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-text">No recent enrollments.</p>
          )}
        </div>
      </section>

      {/* Expiring Subscriptions */}
      <section className="section">
        <div className="section-container">
          <div className="section-header-row">
            <h2 className="section-title">Expiring Subscriptions</h2>
            <Link to="/admin/subscriptions?renewal=expiring-soon" className="btn btn-outline btn-sm">
              View All
            </Link>
          </div>
          {expiringRows.length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Company</th>
                  <th>Tier</th>
                  <th>Expires</th>
                  <th>Days Left</th>
                  <th>Renewal</th>
                  <th>Utilization</th>
                </tr>
              </thead>
              <tbody>
                {expiringRows.slice(0, 10).map((r) => (
                  <tr key={r.sub.id}>
                    <td>
                      {r.sub.customerName}
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{r.sub.customerEmail}</div>
                    </td>
                    <td>{r.sub.companyName || '-'}</td>
                    <td>{r.sub.tier}</td>
                    <td>{formatDate(r.sub.expirationDate)}</td>
                    <td>{r.daysLeft < 0 ? `${Math.abs(r.daysLeft)} overdue` : r.daysLeft}</td>
                    <td>
                      <span className={`status-badge renewal-badge-${r.renewalStatus}`}>
                        {getRenewalLabel(r.renewalStatus)}
                      </span>
                    </td>
                    <td>{r.utilization.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-text">No subscriptions expiring in the next 90 days.</p>
          )}
        </div>
      </section>

      {/* Upcoming Sessions */}
      <section className="section section-alt">
        <div className="section-container">
          <div className="section-header-row">
            <h2 className="section-title">Upcoming Sessions</h2>
            <Link to="/admin/schedules" className="btn btn-outline btn-sm">View All</Link>
          </div>
          {upcomingSessions.length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Enrollment</th>
                </tr>
              </thead>
              <tbody>
                {upcomingSessions.map((s) => (
                  <tr key={s.id}>
                    <td>{courses[s.courseId]?.title || s.courseId}</td>
                    <td>{formatDate(s.sessionDate)}</td>
                    <td>{s.startTime} - {s.endTime}</td>
                    <td>{s.currentEnrollment} / {s.maxSeats}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-text">No upcoming sessions.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
